import { useMutation, useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import { getOrder, getOrderMessages } from '../services/query';
import { useState } from 'react';
import { Message, Party } from '../types';
import { useTranslation } from 'react-i18next';
import { getNotifications } from 'views/email-templates/services/query';
import { Notifications } from 'views/email-templates/types';
import { sendNotification } from '../services/mutations';
import useAlert from 'utils/alert';

export const useNotifications = () => {
  const { id } = useParams<{ id: string }>();
  const [messages, setMessages] = useState<Message[]>([]);
  const [notifications, setNotifications] = useState<Notifications[]>([]);
  const [parties, setParties] = useState<Party[]>([]);
  const [fetchNotifications, setFetchNotifications] = useState(true);
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<Message | undefined>();
  const showAlert = useAlert();

  const { isLoading, isFetching } = useQuery(
    ['orderNotifications', id],
    () => getOrderMessages(id),
    {
      enabled: !!id && fetchNotifications,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        if (data) {
          setFetchNotifications(false);
          setMessages(data.getOrderMessages);
        }
      },
      onError: () => {
        setFetchNotifications(false);
      },
    }
  );

  useQuery(['notifications'], getNotifications, {
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      if (data) {
        setNotifications(data.getNotifications.sort((a, b) => a.subject.localeCompare(b.subject)));
      }
    },
  });

  useQuery(['parties', id], () => getOrder(id), {
    refetchOnWindowFocus: false,
    enabled: !!id,
    onSuccess: (data) => {
      if (data) {
        const partiesWithEmails = data.getOrder.parties
          .map((party) => {
            const seenAddresses = new Set<string>();
            const emails = (party.emails ?? []).filter((email) => {
              if (!email.address || seenAddresses.has(email.address)) return false;
              seenAddresses.add(email.address);
              return true;
            });

            return { ...party, emails };
          })
          .filter((party) => party.emails.length > 0);

        setParties(partiesWithEmails.sort((a, b) => a.name.localeCompare(b.name)));
      }
    },
  });

  const { mutate: sendNotificationMutation, ...sendNotificationResponse } = useMutation(
    sendNotification,
    {
      onSuccess: () => {
        // The dialog stays locked (isSending) until the delayed refetch closes it.
        setTimeout(() => {
          setFetchNotifications(true);
          setOpen(false);
          showAlert(t('messages:send-notification'), 'success');
          sendNotificationResponse.reset();
        }, 3000);
      },
    }
  );

  const isSending = sendNotificationResponse.isLoading || sendNotificationResponse.isSuccess;

  const toggleOpen = () => {
    if (isSending) return;
    setOpen(!open);
  };

  const closeMessage = () => {
    setSelectedMessage(undefined);
  };

  const hanldeSubmitNotification = (data) => {
    sendNotificationMutation({ ...data, orderId: id });
  };

  return {
    messages,
    isLoading,
    isFetching,
    t,
    toggleOpen,
    open,
    notifications,
    parties,
    hanldeSubmitNotification,
    isSending,
    selectedMessage,
    setSelectedMessage,
    closeMessage,
  };
};
